import { Menu } from "@base-ui/react/menu";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "@/utils/cn";

const DropdownMenu = Menu.Root;
const DropdownMenuGroup = Menu.Group;
const DropdownMenuPortal = Menu.Portal;
const DropdownMenuSub = Menu.SubmenuRoot;
const DropdownMenuRadioGroup = Menu.RadioGroup;

// CVA variant definitions for Dropdown sub-components
const dropdownMenuContentVariants = cva(
  [
    "origin-[var(--transform-origin)]",
    "z-50 rounded-md border p-1 shadow-md",
    "max-w-[90vw] max-h-[85vh] overflow-auto",
    "bg-white border-gray-200",
    "dark:bg-gray-950 dark:border-gray-800",
  ],
  {
    defaultVariants: { width: "default" },
    variants: {
      width: {
        default: "min-w-[8rem]",
        lg: "min-w-[12rem]",
        md: "min-w-[10rem]",
        sm: "min-w-[6rem]",
        xl: "min-w-[16rem]",
      },
    },
  },
);

const dropdownMenuItemVariants = cva(
  [
    "relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none",
    "data-highlighted:bg-gray-100 data-highlighted:text-gray-900",
    "data-disabled:pointer-events-none data-disabled:opacity-50",
    "dark:data-highlighted:bg-gray-800 dark:data-highlighted:text-gray-50",
  ],
  {
    defaultVariants: { inset: false },
    variants: { inset: { false: "", true: "pl-8" } },
  },
);

const dropdownMenuLabelVariants = cva(
  ["px-2 py-1.5 text-sm font-semibold", "text-gray-500 dark:text-gray-400"],
  {
    defaultVariants: { inset: false },
    variants: { inset: { false: "", true: "pl-8" } },
  },
);

const dropdownMenuCheckboxItemVariants = cva([
  "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none",
  "data-highlighted:bg-gray-100 data-highlighted:text-gray-900",
  "data-disabled:pointer-events-none data-disabled:opacity-50",
  "dark:data-highlighted:bg-gray-800 dark:data-highlighted:text-gray-50",
]);

const dropdownMenuRadioItemVariants = cva([
  "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none",
  "data-highlighted:bg-gray-100 data-highlighted:text-gray-900",
  "data-disabled:pointer-events-none data-disabled:opacity-50",
  "dark:data-highlighted:bg-gray-800 dark:data-highlighted:text-gray-50",
]);

const dropdownMenuSubContentVariants = cva([
  "z-50 min-w-[8rem] rounded-md border p-1 shadow-lg",
  "max-w-[90vw] max-h-[85vh] overflow-auto",
  "bg-white border-gray-200",
  "dark:bg-gray-950 dark:border-gray-800",
]);

const dropdownMenuSubTriggerVariants = cva(
  [
    "flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none",
    "data-highlighted:bg-gray-100 data-highlighted:text-gray-900 data-popup-open:bg-gray-100 data-popup-open:text-gray-900",
    "dark:data-highlighted:bg-gray-800 dark:data-highlighted:text-gray-50 dark:data-popup-open:bg-gray-800 dark:data-popup-open:text-gray-50",
  ],
  {
    defaultVariants: { inset: false },
    variants: { inset: { false: "", true: "pl-8" } },
  },
);

const dropdownMenuSeparatorVariants = cva(
  "-mx-1 my-1 h-px bg-gray-200 dark:bg-gray-700",
);
const dropdownMenuShortcutVariants = cva(
  "ml-auto text-xs tracking-widest opacity-60",
);
const dropdownMenuItemIndicatorVariants = cva(
  "absolute left-2 flex h-3.5 w-3.5 items-center justify-center",
);
const dropdownMenuCheckIconVariants = cva(
  "h-4 w-4 text-theme-primary dark:text-theme-primary",
);
const dropdownMenuRadioIconVariants = cva(
  "h-2 w-2 fill-current text-theme-primary dark:text-theme-primary",
);
const dropdownMenuSubTriggerChevronVariants = cva("ml-auto h-4 w-4");

/** Trigger. Keeps Radix-style `asChild` by mapping to Base UI `render`. */
export interface DropdownMenuTriggerProps extends Menu.Trigger.Props {
  /** Render the trigger as its single child element. */
  asChild?: boolean;
}

const DropdownMenuTrigger = ({
  asChild,
  children,
  ref,
  ...props
}: DropdownMenuTriggerProps) => {
  if (asChild && React.isValidElement(children)) {
    return <Menu.Trigger ref={ref} {...props} render={children} />;
  }
  return (
    <Menu.Trigger ref={ref} {...props}>
      {children}
    </Menu.Trigger>
  );
};

/**
 * Props for the DropdownMenuContent component.
 * Positioning props route to Menu.Positioner; everything else to Menu.Popup.
 */
export interface DropdownMenuContentProps
  extends Menu.Popup.Props, VariantProps<typeof dropdownMenuContentVariants> {
  align?: Menu.Positioner.Props["align"];
  side?: Menu.Positioner.Props["side"];
  sideOffset?: Menu.Positioner.Props["sideOffset"];
  alignOffset?: Menu.Positioner.Props["alignOffset"];
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * The dropdown content component that contains menu items.
 * Constrained to 90% viewport width and 85% viewport height with overflow scrolling.
 */
const DropdownMenuContent = ({
  className,
  width = "default",
  align = "center",
  side,
  sideOffset = 4,
  alignOffset,
  ref,
  ...props
}: DropdownMenuContentProps) => (
  <Menu.Portal>
    <Menu.Positioner
      align={align}
      alignOffset={alignOffset}
      side={side}
      sideOffset={sideOffset}
    >
      <Menu.Popup
        className={cn(dropdownMenuContentVariants({ width }), className)}
        ref={ref}
        {...props}
      />
    </Menu.Positioner>
  </Menu.Portal>
);

interface DropdownMenuItemProps
  extends
    Omit<Menu.Item.Props, "onSelect">,
    VariantProps<typeof dropdownMenuItemVariants> {
  /**
   * Radix-style select handler; forwarded to Base UI onClick.
   *
   * Base UI 1.7 added its own `onSelect`, typed against SyntheticEvent, which
   * is why this omits it rather than widening: keeping the click mapping
   * preserves the behaviour this shim has always had. Adopting Base UI's
   * `onSelect` would also fire on keyboard selection, which is a behaviour
   * change and deliberately not made here.
   */
  onSelect?: Menu.Item.Props["onClick"];
}

const DropdownMenuItem = ({
  className,
  inset = false,
  onSelect,
  onClick,
  ref,
  ...props
}: DropdownMenuItemProps) => (
  <Menu.Item
    className={cn(dropdownMenuItemVariants({ inset }), className)}
    onClick={onSelect ?? onClick}
    ref={ref}
    {...props}
  />
);

interface DropdownMenuLabelProps
  extends
    React.ComponentPropsWithoutRef<"div">,
    VariantProps<typeof dropdownMenuLabelVariants> {
  ref?: React.Ref<HTMLDivElement>;
}

// Base UI has no standalone menu label (Menu.GroupLabel requires a Menu.Group
// ancestor and throws otherwise), so render a plain div.
const DropdownMenuLabel = ({
  className,
  inset = false,
  ref,
  ...props
}: DropdownMenuLabelProps) => (
  <div
    className={cn(dropdownMenuLabelVariants({ inset }), className)}
    ref={ref}
    {...props}
  />
);

interface DropdownMenuCheckboxItemProps extends Menu.CheckboxItem.Props {
  ref?: React.Ref<HTMLDivElement>;
}

const DropdownMenuCheckboxItem = ({
  className,
  children,
  checked,
  ref,
  ...props
}: DropdownMenuCheckboxItemProps) => (
  <Menu.CheckboxItem
    checked={checked}
    className={cn(dropdownMenuCheckboxItemVariants(), className)}
    ref={ref}
    {...props}
  >
    <span className={dropdownMenuItemIndicatorVariants()}>
      <Menu.CheckboxItemIndicator>
        <svg
          className={dropdownMenuCheckIconVariants()}
          fill="none"
          height="16"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          width="16"
          xmlns="http://www.w3.org/2000/svg"
        >
          <title>Check</title>
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </Menu.CheckboxItemIndicator>
    </span>
    {children}
  </Menu.CheckboxItem>
);

interface DropdownMenuRadioItemProps extends Menu.RadioItem.Props {
  ref?: React.Ref<HTMLDivElement>;
}

const DropdownMenuRadioItem = ({
  className,
  children,
  ref,
  ...props
}: DropdownMenuRadioItemProps) => (
  <Menu.RadioItem
    className={cn(dropdownMenuRadioItemVariants(), className)}
    ref={ref}
    {...props}
  >
    <span className={dropdownMenuItemIndicatorVariants()}>
      <Menu.RadioItemIndicator>
        <svg
          className={dropdownMenuRadioIconVariants()}
          fill="none"
          height="16"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          width="16"
          xmlns="http://www.w3.org/2000/svg"
        >
          <title>Circle</title>
          <circle cx="12" cy="12" r="10" />
        </svg>
      </Menu.RadioItemIndicator>
    </span>
    {children}
  </Menu.RadioItem>
);

interface DropdownMenuSubContentProps extends Menu.Popup.Props {
  ref?: React.Ref<HTMLDivElement>;
}

const DropdownMenuSubContent = ({
  className,
  ref,
  ...props
}: DropdownMenuSubContentProps) => (
  <Menu.Portal>
    <Menu.Positioner>
      <Menu.Popup
        className={cn(dropdownMenuSubContentVariants(), className)}
        ref={ref}
        {...props}
      />
    </Menu.Positioner>
  </Menu.Portal>
);

interface DropdownMenuSubTriggerProps
  extends
    Menu.SubmenuTrigger.Props,
    VariantProps<typeof dropdownMenuSubTriggerVariants> {
  ref?: React.Ref<HTMLDivElement>;
}

const DropdownMenuSubTrigger = ({
  className,
  inset = false,
  children,
  ref,
  ...props
}: DropdownMenuSubTriggerProps) => (
  <Menu.SubmenuTrigger
    className={cn(dropdownMenuSubTriggerVariants({ inset }), className)}
    ref={ref}
    {...props}
  >
    {children}
    <svg
      className={dropdownMenuSubTriggerChevronVariants()}
      fill="none"
      height="16"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width="16"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>Chevron Right</title>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  </Menu.SubmenuTrigger>
);

interface DropdownMenuSeparatorProps extends Menu.Separator.Props {
  ref?: React.Ref<HTMLDivElement>;
}

const DropdownMenuSeparator = ({
  className,
  ref,
  ...props
}: DropdownMenuSeparatorProps) => (
  <Menu.Separator
    className={cn(dropdownMenuSeparatorVariants(), className)}
    ref={ref}
    {...props}
  />
);

const DropdownMenuShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => (
  <span className={cn(dropdownMenuShortcutVariants(), className)} {...props} />
);

export {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
};
